import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import MediaStoryCard from "./MediaStoryCard";
import { MediaProviderId, PROVIDER_META } from "./MediaPlatformIcons";
import { MOOD_ICONS, MOOD_WISH_ICONS, moodLabelKey, wishLabelKey } from "../utils/moodKeys";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

export type VenueVibeItem = {
  id: string; kind: "vibe" | "real" | "live" | "media"; media_url: string | null; secondary_media_url?: string | null;
  caption?: string | null; created_at: string; author_id: string; author_name: string; author_avatar?: string | null;
  external_provider?: MediaProviderId | null; external_url?: string | null;
  external_title?: string | null; external_thumbnail_url?: string | null;
  mood_key?: string | null; mood_wish_key?: string | null;
};

// Viewer partagé pour "ce qu'il se passe à ce lieu" — vibes/lives/reals mélangés (voir
// GET /venues/:id/vibes), ouvert depuis la fiche du lieu (anneau sur la photo carrée) ou depuis
// la bulle du lieu dans la barre Home. Volontairement plus simple que StoryViewer (pas de Top,
// pas de "vu par", pas de suppression) : ces items viennent de deux tables différentes
// (stories pour vibe/real, profile_photos pour live), StoryViewer ne sait interagir qu'avec la
// première — un carrousel de lecture pure évite d'exposer des actions qui ne marcheraient qu'à
// moitié.
export default function VenueVibesViewer({ items, loading, onClose }: { items: VenueVibeItem[]; loading: boolean; onClose: () => void }) {
  const { lang } = useApp();
  const [index, setIndex] = useState(0);
  const current = items[index];

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.92)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button onClick={onClose} style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", color: "white", fontSize: 26, cursor: "pointer" }}>✕</button>
      {loading ? (
        <div style={{ color: "white" }}>Chargement…</div>
      ) : items.length === 0 || !current ? (
        <div style={{ color: "white", textAlign: "center", padding: 20 }}>Rien à voir ici pour le moment.</div>
      ) : (
        <div onClick={e => e.stopPropagation()} style={{ position: "relative", width: "100%", maxWidth: 420, height: "80vh", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 4px 12px" }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", backgroundColor: "#333", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12 }}>
              {current.author_avatar ? <img src={current.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(current.author_name)}
            </div>
            <div style={{ color: "white", fontSize: 13, fontWeight: 600, flex: 1 }}>{current.author_name}</div>
            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{current.kind === "real" ? "Real Vibe" : current.kind}</span>
          </div>
          <div style={{ position: "relative", flex: 1, borderRadius: 16, overflow: "hidden", backgroundColor: "#111" }}>
            {current.kind === "media" ? (
              <MediaStoryCard provider={current.external_provider!} title={current.external_title || ""} thumbnailUrl={current.external_thumbnail_url} url={null} renderCta={false} />
            ) : current.kind === "live" ? (
              <video src={current.media_url!} controls autoPlay style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <img src={current.media_url!} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
            {current.kind === "real" && current.secondary_media_url && (
              <img src={current.secondary_media_url} alt="" style={{ position: "absolute", top: 14, left: 14, width: 90, height: 120, objectFit: "cover", borderRadius: 10, border: "2px solid white", boxShadow: "0 4px 14px rgba(0,0,0,0.4)" }} />
            )}
            {current.kind === "real" && current.mood_key && (
              <div style={{ position: "absolute", bottom: 14, left: 14, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: current.mood_key === "invite_me" ? "#E8C766" : "white", fontSize: 12.5, fontWeight: 700, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>
                  {(() => { const Icon = MOOD_ICONS[current.mood_key as keyof typeof MOOD_ICONS]; return Icon ? <Icon size={13} strokeWidth={2} /> : null; })()}
                  {t(lang, moodLabelKey(current.mood_key)).toUpperCase()}
                </div>
                {current.mood_key === "invite_me" && current.mood_wish_key && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.85)", fontSize: 11.5 }}>
                    {(() => { const WIcon = MOOD_WISH_ICONS[current.mood_wish_key as keyof typeof MOOD_WISH_ICONS]; return WIcon ? <WIcon size={12} strokeWidth={2} /> : null; })()}
                    {t(lang, wishLabelKey(current.mood_wish_key))}
                  </div>
                )}
              </div>
            )}
            {current.kind === "media" && current.external_url && (
              <div style={{ position: "absolute", bottom: 14, left: 14, right: 14, zIndex: 10, display: "flex", justifyContent: "center" }}>
                <a
                  href={current.external_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.4)", backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
                >
                  {PROVIDER_META[current.external_provider!].verb} sur {PROVIDER_META[current.external_provider!].label} →
                </a>
              </div>
            )}
            {index > 0 && (
              <button onClick={() => setIndex(i => i - 1)} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "none", color: "white", width: 34, height: 34, borderRadius: "50%", cursor: "pointer", fontSize: 16 }}>‹</button>
            )}
            {index < items.length - 1 && (
              <button onClick={() => setIndex(i => i + 1)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "none", color: "white", width: 34, height: 34, borderRadius: "50%", cursor: "pointer", fontSize: 16 }}>›</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 10 }}>
            {items.map((_, i) => (
              <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, backgroundColor: i === index ? "white" : "rgba(255,255,255,0.3)" }} />
            ))}
          </div>
        </div>
      )}
    </div>
  ,
    document.body
  );
}
